// All user-facing strings for findings and fixes. Plain English, sentence case, active voice, no exclamation marks.
// Components and the API read labels from here; nothing else hard-codes them.
import type {
  Category, Confidence, FindingStatus, ItemType, Plan, Provider, RequestStatus, Severity, SourceId,
} from './types';
import type { Exposed, Place } from './places';

export const CATEGORY_LABEL: Record<Category, string> = {
  dark_web: 'Dark web',
  money: 'Money',
  public_profiles: 'Public profiles',
  location: 'Location',
  data_brokers: 'Data brokers',
};

// Where we found it (domain/places.ts): name and one line on what the place is.
export const PLACE_LABEL: Record<Place, string> = {
  breaches: 'Data breaches', transactions: 'Your transactions', profiles: 'Social profiles',
  people_search: 'People-search sites', public_records: 'Public records',
};
export const PLACE_HINT: Record<Place, string> = {
  breaches: 'Hacked and sold on the dark web',
  transactions: 'Charges on your bank accounts',
  profiles: 'Public accounts in your name',
  people_search: 'Sites that sell your details',
  public_records: 'Official public registers',
};

// What it exposes (domain/places.ts exposesOf).
export const EXPOSED_LABEL: Record<Exposed, string> = {
  password: 'Passwords', card: 'Card details', address: 'Home address', phone: 'Phone numbers',
  email: 'Emails', identity: 'Name and age', username: 'Usernames', activity: 'Public activity',
};

export const SOURCE_LABEL: Record<SourceId, string> = {
  dark_web: 'Dark web',
  transactions: 'Transactions',
  osint: 'Footprint scan',
  incogni: 'Incogni',
  coveron: 'Coveron',
};

export const SEVERITY_LABEL: Record<Severity, string> = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };
export const CONFIDENCE_LABEL: Record<Confidence, string> = { confirmed: 'Confirmed', likely: 'Likely match' };

export const STATUS_LABEL: Record<FindingStatus, string> = {
  new: 'New',
  needs_action: 'Needs action',
  in_progress: 'In progress',
  resolved: 'Resolved',
  dismissed: 'Dismissed',
};

export const ITEM_TYPE_LABEL: Record<ItemType, { one: string; many: string }> = {
  email: { one: 'Email', many: 'Emails' },
  phone: { one: 'Phone number', many: 'Phone numbers' },
  card: { one: 'Payment card', many: 'Payment cards' },
  national_id: { one: 'National ID', many: 'National IDs' },
  bank_account: { one: 'Bank account', many: 'Bank accounts' },
  username: { one: 'Username', many: 'Usernames' },
  name: { one: 'Full name', many: 'Names' },
  address: { one: 'Home address', many: 'Home addresses' },
};

export const PLAN_LABEL: Record<Plan, string> = { free_scan: 'Free scan', basic: 'Basic', complete: 'Complete', ultimate: 'Ultimate' };

// Partner fixes are white-labelled: Nord names the job, the partner is credited.
export const PROVIDER_LABEL: Record<Provider, { name: string; credit: string }> = {
  nord: { name: 'Nord Monitor', credit: '' },
  nordpass: { name: 'NordPass', credit: 'with NordPass' },
  incogni: { name: 'Incogni', credit: 'powered by Incogni' },
  coveron: { name: 'Coveron', credit: 'by Coveron' },
  threat_protection: { name: 'Threat Protection', credit: 'with Threat Protection' },
};

export const REQUEST_STATUS_LABEL: Record<RequestStatus, string> = {
  queued: 'Queued',
  sent: 'Sent',
  confirmed: 'Confirmed',
  needs_user: 'Needs you',
};

export const DISMISS_REASONS = {
  not_me: 'This is not me',
  already_fixed: 'I already fixed this',
  not_important: 'I am not worried about this',
  other: 'Something else',
} as const;
export type DismissReason = keyof typeof DISMISS_REASONS;

// Finding report (/findings/:id): section titles and the lines that are not per fix kind.
export const REPORT_COPY = {
  exposed: 'What was exposed',
  exposedHint: 'The kinds of personal data in this record. The exact values are in the next section.',
  values: 'Exact values',
  valuesHint: 'Hidden until you confirm it is you with a code.',
  why: 'Why it matters',
  next: 'What to do next',
  closedNext: 'How it was fixed',
  more: 'More information',
  resolved: (date: string) => (date ? `Resolved on ${date}` : 'Resolved'),
  dismissed: 'You dismissed this finding',
  resolvedNote: 'It no longer counts against your score.',
  noFix: 'Nothing to do. We keep watching in case it changes.',
  // Done-for-you fixes before the request starts: what happens, in order.
  dfySteps: (site: string, partner: string) => [
    { title: 'You approve the request', detail: 'One signature covers every removal we send for you.' },
    { title: partner ? `${partner} sends the opt-out to ${site}` : `We ask ${site} to take it down`, detail: 'Sites usually answer within two weeks.' },
    { title: `${site} confirms the removal`, detail: 'We check again later to make sure it stays down.' },
  ],
} as const;

export const LOCKED_VALUE = '••••••••••';
export const LOCKED_UNLOCK_IN = (plan: Plan) => `Unlock in ${PLAN_LABEL[plan]}`;

// Fix copy per finding kind (see fixes.ts). `{site}` is replaced with the company from the finding title.
export const FIX_COPY = {
  breach_password: {
    verb: 'Change password',
    summary: 'Your password for {site} is out there. Change it and anywhere you reused it.',
    steps: [
      { title: 'Change your {site} password', detail: 'Use a new password you have never used before.', action: 'Change in NordPass' },
      { title: 'Turn on two-factor authentication', detail: 'A code on your phone stops anyone who has your password.', action: 'Turn on 2FA' },
      { title: 'Change it where you reused it', detail: 'Any account with the same password is exposed too.' },
    ],
  },
  breach_email: {
    verb: 'Secure account',
    summary: 'Your email from the {site} breach can be used for phishing. Lock the account down.',
    steps: [
      { title: 'Turn on two-factor authentication for {site}', action: 'Turn on 2FA' },
      { title: 'Watch for emails that mention {site}', detail: 'Phishing often follows a breach. Do not click links that ask you to sign in.' },
    ],
  },
  stealer_log: {
    verb: 'Clean your device',
    summary: 'Malware copied saved logins from a device. Clean it before you change passwords.',
    steps: [
      { title: 'Scan the device for malware', action: 'Scan with Threat Protection' },
      { title: 'Sign out of all sessions', detail: 'Stolen cookies keep working until you sign out everywhere.' },
      { title: 'Change the passwords saved in your browser', action: 'Change in NordPass' },
    ],
  },
  card_leak: {
    verb: 'Freeze card',
    summary: 'Your card details are exposed. Freeze the card and order a new one.',
    steps: [
      { title: 'Freeze the card in your banking app', action: 'Call your bank' },
      { title: 'Order a replacement card' },
      { title: 'Check recent transactions', detail: 'Report anything you do not recognise.' },
    ],
  },
  id_leak: {
    verb: 'Protect your identity',
    summary: 'Your ID number is exposed. Watch your credit and report the leak.',
    steps: [
      { title: 'Report the exposed ID to the issuing office' },
      { title: 'Turn on credit monitoring', action: 'Turn on with Coveron' },
    ],
  },
  phone_leak: {
    verb: 'Secure your number',
    summary: 'Your phone number is exposed. Protect it against SIM swaps and scam texts.',
    steps: [
      { title: 'Set a SIM PIN with your carrier', action: 'Call your carrier' },
      { title: 'Ignore texts that ask for codes or payments' },
    ],
  },
  duplicate_charge: {
    verb: 'Dispute charge',
    summary: 'You were charged twice. Ask your bank to reverse the second charge.',
    steps: [
      { title: 'Check you did not make the second payment' },
      { title: 'Ask your bank to reverse it', action: 'Call your bank' },
    ],
  },
  unusual_time: {
    verb: 'Review charge',
    summary: 'A charge came through at an unusual hour. Check that it was you.',
    steps: [
      { title: 'Confirm the charge was yours' },
      { title: 'If not, block the account payment and report it', action: 'Call your bank' },
    ],
  },
  unusual_frequency: {
    verb: 'Review charges',
    summary: 'The same merchant charged you more often than usual. Check each charge.',
    steps: [
      { title: 'Match each charge to a purchase' },
      { title: 'Report the ones you do not recognise', action: 'Call your bank' },
    ],
  },
  public_profile: {
    verb: 'Request removal',
    summary: 'A public profile shows details about you. We ask the site to hide or remove it.',
    steps: [{ title: 'Approve the removal request' }],
  },
  location: {
    verb: 'Remove address',
    summary: 'Your home address is published. We ask the site to take it down.',
    steps: [{ title: 'Approve the removal request' }],
  },
  broker_listing: {
    verb: 'Remove listing',
    summary: 'A data broker sells your details. Incogni sends the opt-out and follows up.',
    steps: [{ title: 'Approve the opt-out request' }],
  },
} as const;

// Paywall headlines, one per upgrade trigger (entry_point). Phase 6 adds the rest of the moments from the brief.
export const PAYWALL_HEADLINE: Record<string, string> = {
  map_not_scanned_money: 'Watch your bank accounts for charges you did not make',
  map_not_scanned_public_profiles: 'See which public profiles show where you live',
  map_not_scanned_location: 'Find where your home address is published',
  map_not_scanned_data_brokers: 'Find the data brokers selling your details',
  map_not_scanned_dark_web: 'Watch the dark web for your details',
  coverage_meter: 'Watch everything that can expose you, not just your emails',
};
export const paywallHeadline = (entryPoint: string) =>
  PAYWALL_HEADLINE[entryPoint] ?? (entryPoint.startsWith('coverage_') ? PAYWALL_HEADLINE.coverage_meter : 'Unlock everything Nord Monitor can watch');

export const NOT_SCANNED = (plan: Plan) => `Not scanned · ${PLAN_LABEL[plan]}`;

export const RECENCY_LABEL = { week: 'This week', month: 'This month', quarter: 'Last 3 months', older: 'Older than 90 days' } as const;
export const TAB_LABEL = { needs_action: 'Needs action', in_progress: 'In progress', resolved: 'Resolved' } as const;

// ---------- Resolve ----------
export const PARTNER_COPY = {
  incogni: { title: 'Remove your data from data brokers', credit: 'powered by Incogni', body: 'Incogni sends opt-out requests to every broker selling your details and keeps checking they stay removed.' },
  coveron: { title: 'Credit monitoring and identity theft insurance', credit: 'by Coveron', body: 'Coveron watches your credit file for new accounts in your name and covers recovery costs if your identity is stolen.' },
} as const;

export const TASK_COPY: Record<string, { title: string; action: string }> = {
  default: { title: 'A site needs you before it removes your data', action: 'Mark as done' },
  identity: { title: 'Confirm your identity so {site} removes your address', action: 'I confirmed it' },
};

// Done-for-you authorisation: plain summary first, the document second, one signature.
export const AUTHORISATION = {
  title: 'Let us act for you',
  summary: [
    'We contact sites and data brokers in your name and ask them to remove your details.',
    'We share only what they need to find you: your name, address and the email or phone on the listing.',
    'We never pay anyone or agree to anything else in your name.',
    'You can withdraw this at any time in Settings. Requests already sent keep going.',
  ],
  document: [
    'Authorisation to act on your behalf (version 2026-10)',
    'I authorise Nord Security and its partners, including Incogni, to submit data removal and opt-out requests on my behalf under the GDPR, the UK GDPR and equivalent laws, to the organisations listed in my Nord Monitor account.',
    'This authorisation covers removal, restriction and objection requests only. It does not cover legal proceedings, payments or contracts.',
    'It lasts until I withdraw it. Withdrawing it stops new requests; requests already sent continue until the organisation replies.',
  ],
  signLabel: 'Type your full name to sign',
  cta: 'Sign and start',
} as const;

Object.assign(PAYWALL_HEADLINE, {
  partner_incogni: 'Remove your data from data brokers, done for you',
  partner_coveron: 'Watch your credit and insure your identity',
  locked_do_it_for_me: 'Let us send the removal requests for you',
  locked_fix: 'Get step-by-step help to fix this',
  locked_finding: 'See what was exposed and how to fix it',
});

// ---------- Watchlist ----------
// Display masking for identifiers the user adds (the API stores what it is given; the UI never shows it raw).
export function maskIdentifier(type: ItemType, value: string): string {
  const v = value.trim();
  if (type === 'email') {
    const [user, domain] = v.split('@');
    if (!domain) return v;
    return `${user[0] ?? ''}•••${user.length > 1 ? user[user.length - 1] : ''}@${domain}`;
  }
  if (type === 'phone') {
    const digits = v.replace(/[^\d+]/g, '');
    // Country code as typed ("+46 73…"); without a space, assume two digits.
    const cc = v.match(/^\+\d{1,3}(?=[\s-])/)?.[0] ?? (digits.startsWith('+') ? digits.slice(0, 3) : '');
    return `${cc} ••• ••• ${digits.slice(-2)}`.trim();
  }
  if (type === 'card' || type === 'bank_account') return `•••• ${v.replace(/\s/g, '').slice(-4)}`;
  if (type === 'name' || type === 'address') return v;
  return `${v[0] ?? ''}•••${v.slice(-1)}`;
}

export const ITEM_PLACEHOLDER: Record<ItemType, string> = {
  email: 'name@example.com', phone: '+44 7700 900123', card: 'Card number', national_id: 'ID number',
  bank_account: 'IBAN or account number', username: 'Username', name: 'Full name', address: 'Street, city',
};

export const SCAN_SOURCES: Record<ItemType, string[]> = {
  email: ['Dark web', 'Breach archives'], phone: ['Dark web', 'Caller ID sites', 'Data brokers'], card: ['Dark web', 'Card markets'],
  national_id: ['Dark web'], bank_account: ['Transactions'], username: ['Public profiles', 'Dark web'], name: ['Data brokers', 'Public profiles'], address: ['Data brokers', 'Address registries'],
};

Object.assign(PAYWALL_HEADLINE, {
  watchlist_quota: 'Watch more than 5 emails',
  footprint_scan: 'See where your details are published online',
  watchlist_type_phone: 'Watch your phone number for leaks and scam lists',
  watchlist_type_card: 'Watch your cards for leaks on the dark web',
  watchlist_type_national_id: 'Watch your ID number for identity theft',
  watchlist_type_bank_account: 'Watch your bank account for charges you did not make',
  watchlist_type_username: 'Find your usernames on public sites and leaks',
  watchlist_type_name: 'Find where your name is published',
  watchlist_type_address: 'Find where your home address is published',
});

// Preview items (a type the plan does not cover): we scan it, show what was found, details unlock with the plan.
Object.assign(PAYWALL_HEADLINE, {
  locked_item_email: 'See what your email exposed and how to fix it',
  locked_item_phone: 'See what we found for your phone number and how to fix it',
  locked_item_card: 'See where your card leaked and how to block it',
  locked_item_national_id: 'See where your ID number leaked and how to protect it',
  locked_item_bank_account: 'See the charges we flagged on your bank account',
  locked_item_username: 'See where your username shows up and how to fix it',
  locked_item_name: 'See where your name is published and how to remove it',
  locked_item_address: 'See where your home address is published and how to remove it',
});
export const PREVIEW_COPY = {
  hint: (plan: Plan) => `Preview on ${PLAN_LABEL[plan]}`,
  hintLabel: (many: string, plan: Plan, full: Plan) => `${many}: preview on ${PLAN_LABEL[plan]}, watched in full in ${PLAN_LABEL[full]}`,
  seeDetails: (full: Plan) => `See details in ${PLAN_LABEL[full]}`,
  used: (one: string, full: Plan) => `You already preview one ${one}. Watch more in ${PLAN_LABEL[full]}.`,
  addNote: (full: Plan) => `We scan it now and show what we find. Details and fixes unlock in ${PLAN_LABEL[full]}.`,
  evidence: (full: Plan) => `Evidence unlocks in ${PLAN_LABEL[full]}.`,
  ask: 'The details are locked on your plan. Unlock them to see where this came from.',
} as const;

// ---------- Free scan ----------
export const FREE_SCAN = {
  title: 'See what is out there about you',
  intro: 'A free, one-time scan of the dark web and public sources. Every identifier is verified with a code first.',
  signIn: 'Continue with Nord Account',
  identify: 'Add what you want us to scan',
  identifyHint: 'We only scan identifiers you prove are yours.',
  start: 'Start free scan',
  resultTitle: 'Your exposure',
  lockedLine: 'Values, evidence and fixes are locked on the free scan.',
  cta: 'See plans',
} as const;

Object.assign(PAYWALL_HEADLINE, {
  free_scan_result: 'Fix what we found and keep watching',
});

// ---------- Screen states (?state=loading|empty|error|partial|offline) ----------
export type ScreenId = 'footprint' | 'findings' | 'finding' | 'resolve' | 'watchlist' | 'settings' | 'scan';
export const SCREEN_STATES: Record<ScreenId, {
  name: string;
  empty: { title: string; body: string; action: { label: string; to: string } };
  partial: string; // the source that failed
}> = {
  footprint: { name: 'your footprint', partial: 'Transaction monitoring',
    empty: { title: 'Nothing watched yet', body: 'Add an email and we scan the dark web for it straight away.', action: { label: 'Add to Watchlist', to: '/watchlist' } } },
  findings: { name: 'your findings', partial: 'The footprint scan',
    empty: { title: 'No findings', body: 'We found nothing on what you watch. Add more to widen the scan.', action: { label: 'Add to Watchlist', to: '/watchlist' } } },
  finding: { name: 'this report', partial: 'Breach details',
    empty: { title: 'This report is gone', body: 'It was resolved or dismissed, or the link is old.', action: { label: 'Back to findings', to: '/findings' } } },
  resolve: { name: 'your fixes', partial: 'Incogni',
    empty: { title: 'Nothing to fix', body: 'When a finding needs work, its fix shows up here.', action: { label: 'See your findings', to: '/findings' } } },
  watchlist: { name: 'your Watchlist', partial: 'The dark web scan',
    empty: { title: 'Your Watchlist is empty', body: 'Add the email you use most. We send a code to confirm it is yours.', action: { label: 'Add an email', to: '/watchlist' } } },
  settings: { name: 'your settings', partial: 'Plan details',
    empty: { title: 'No settings to show', body: 'Sign in again to load your settings.', action: { label: 'Go to your footprint', to: '/' } } },
  scan: { name: 'the free scan', partial: 'The public profile search',
    empty: { title: 'Nothing to scan yet', body: 'Add an email to start your free scan.', action: { label: 'Start free scan', to: '/scan' } } },
};
export const PARTIAL_LINE = (source: string) => `${source} did not respond. Everything else is up to date; we retry in a few minutes.`;
export const OFFLINE_LINE = (time: string) => `You are offline. Showing what we saved at ${time}. Changes wait until you are back.`;

// ---------- Settings ----------
export const SETTINGS = {
  cancel: {
    title: 'Cancel your plan',
    keep: 'You keep everything until the end of your billing period. We keep your findings and Watchlist for 30 days after that in case you come back.',
    stops: ['Continuous dark web and transaction monitoring', 'Monthly footprint scans', 'Done-for-you removal requests'],
    confirmTitle: 'Confirm cancellation',
    confirm: (date: string) => `Your plan ends on ${date}. You will not be charged again.`,
    done: (date: string) => `Cancelled. Your plan stays active until ${date}.`,
  },
  digest: { weekly: 'Weekly', monthly: 'Monthly', off: 'Off' },
  alerts: { new_findings: 'New findings', needs_you: 'Requests that need you', score_change: 'Score changes' },
} as const;
Object.assign(PAYWALL_HEADLINE, { settings_plan: 'Choose the plan that covers what matters to you' });

// What a fix changes, said as risk (not points). `{site}` → company. risk = what stays possible if left; fixed = what changes.
export const FIX_OUTCOME: Record<keyof typeof FIX_COPY, { risk: string; fixed: string }> = {
  breach_password: { risk: 'Anyone with the leaked list can sign in to {site} as you, and to any account that uses the same password.', fixed: 'The leaked password stops working, so the list is useless against you.' },
  breach_email: { risk: 'Scammers can use your email from the {site} leak to send convincing phishing.', fixed: 'Even a convincing phishing email cannot get into your {site} account.' },
  stealer_log: { risk: 'Stolen sessions can still open your accounts without a password.', fixed: 'Stolen sessions are signed out and the device is clean.' },
  card_leak: { risk: 'Your card details can be used for purchases you did not make.', fixed: 'The leaked card number no longer works anywhere.' },
  id_leak: { risk: 'Someone could open credit or accounts in your name.', fixed: 'New credit in your name gets flagged before it goes through.' },
  phone_leak: { risk: 'Your number can be hijacked with a SIM swap or flooded with scam texts.', fixed: 'Your number cannot be moved to another SIM without your PIN.' },
  duplicate_charge: { risk: 'You stay out of pocket for a payment you did not make.', fixed: 'The second charge is returned to your account.' },
  unusual_time: { risk: 'If it was not you, someone can keep taking money from your account.', fixed: 'You know the charge was yours, or your bank has stopped it.' },
  unusual_frequency: { risk: 'Repeated charges can keep draining the account.', fixed: 'Only the charges you made stay on your account.' },
  public_profile: { risk: 'Strangers can see where you live and run.', fixed: 'The profile no longer shows where you live.' },
  location: { risk: 'Anyone can look up your home address in seconds.', fixed: 'Your home address disappears from that site.' },
  broker_listing: { risk: '{site} sells your name, age and address to anyone who pays.', fixed: '{site} stops selling your details, and Incogni checks it stays that way.' },
};

// ---------- Onboarding (/welcome) ----------
export const ONBOARDING = {
  welcome: {
    title: 'See your risk, fix it and watch it fall',
    intro: 'Nord Monitor finds where your details leaked or are published, helps you clean them up and keeps watch.',
    features: [
      { icon: 'shield-halved', title: 'Dark web monitoring, every day', body: 'We check breach dumps and criminal markets for your emails, phone and cards daily, and tell you when something new turns up.' },
      { icon: 'building-columns', title: 'Transaction monitoring', body: 'We flag duplicate charges and payments you did not make on the bank accounts you connect.' },
      { icon: 'globe', title: 'Footprint scan', body: 'Once a month we search public profiles, people-search sites and address registries for your name and city.' },
      { icon: 'list-check', title: 'Fixes, guided or done for you', body: 'Step-by-step help with NordPass for passwords and 2FA. Data broker removals handled for you, powered by Incogni.' },
    ],
    control: {
      title: 'Your data, your control',
      body: 'What we keep and what you can do with it.',
      points: [
        'We never sell your data or use it for ads.',
        'Export or delete everything any time in Settings.',
        'We keep the details you ask us to watch and what our scans find. Leaked values stay hidden until you confirm it is you with a code.',
      ],
    },
  },
  how: {
    title: 'Three steps to your first picture',
    intro: 'About two minutes in total. You can stop after any step.',
    steps: [
      { title: 'Quick scan', body: 'We check the dark web for your email and name. It takes about a minute.' },
      { title: 'Your first summary', body: 'Your score, what leaked and the one thing to fix first.' },
      { title: 'Deep scan', body: 'We search public profiles, people-search sites and address registries for what is published about you.' },
    ],
  },
  name: {
    title: 'What is your name?',
    intro: 'We search for it the way it appears in records.',
    why: 'Used only to find your exposure',
    fromAccount: 'From your Nord Account · verified',
    first: 'First name', middle: 'Middle name', last: 'Last name', optional: 'Optional',
    firstError: 'Enter your first name.', lastError: 'Enter your last name.',
  },
  place: {
    title: 'Where do you live?',
    intro: 'Data brokers and registries are local, so we start where you are.',
    country: 'Country', countryPlaceholder: 'Choose your country', countrySearch: 'Search countries', countryError: 'Choose your country to start the scan.',
    noCountry: 'No country matches that search.',
    city: 'City', cityHint: 'Only your city, never your street.',
    start: 'Start quick scan', duration: 'The quick scan takes about a minute.',
  },
  trust: { title: 'Built on trust', body: 'We never sell your data, and you can delete it any time.' },
  quick: {
    running: 'Checking the dark web', resultTitle: 'Your first summary',
    exposures: (n: number) => `${n} ${n === 1 ? 'exposure' : 'exposures'} found`,
    mostExposed: 'Most exposed',
    mostExposedNote: 'Counted from the records this scan found, one per record.',
    whereTitle: 'Where it leaked',
    more: (n: number) => `+${n} more on your dashboard`,
    next: 'Continue to deep scan', skip: 'Skip for now',
    later: 'Add your phone, cards and usernames later from Watchlist.',
  },
  deep: {
    running: 'Searching the public web', sources: ['Public profiles', 'People-search sites', 'Address registries', 'Search engines', 'Account lookups'],
    resultTitle: 'What is public about you',
    names: 'Names', usernames: 'Usernames', places: 'Places', accounts: 'Accounts found', profiles: 'Public profiles',
    accountsNote: 'Sign-up pages on these sites confirm an account for your email. Phishers use lists like this to choose who to imitate.',
    showAll: (n: number) => `Show all ${n}`, showLess: 'Show fewer',
    meaning: 'What it means', first: 'Fix this first',
    finish: 'Go to your dashboard',
  },
  close: 'Close and go to your dashboard', back: 'Back', next: 'Continue',
} as const;

export const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
  'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina',
  'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 'Central African Republic',
  'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czechia', 'Democratic Republic of the Congo',
  'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini',
  'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea',
  'Guinea-Bissau', 'Guyana', 'Haiti', 'Honduras', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy',
  'Ivory Coast', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Lebanon',
  'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta',
  'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique',
  'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway',
  'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar',
  'Romania', 'Russia', 'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino',
  'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands',
  'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan',
  'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
  'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela',
  'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
] as const;
